import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MainFormErrorMessage,
} from '@/components/ui/form'
import { FormInput } from '@/components/ui/form/FormInput'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { zodResolver } from '@hookform/resolvers/zod'
import ReactJsonView from '@microlink/react-json-view'
import {
  createIntegrationSchema,
  type CreateIntegrationSchema,
} from '@repo/shared/schemas/integrations'
import type { WebhookArticlePublishedPayload } from '@repo/shared/schemas/integrations/custom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Copy, CopyCheck, RefreshCcw, Send } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute(
  '/_dashboard/settings/integrations/custom',
)({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    // Ensure the integrations query is data is available before the route is loaded
    // This shows the data instantly when the UI loads
    await context.queryClient.ensureQueryData(
      orpc.integrations.getIntegrations.queryOptions(),
    )
  },
})

const webhookPayloadExample: WebhookArticlePublishedPayload = {
  event_type: 'article.published',
  event_id: 'event_123',
  timestamp: '2024-01-15T10:30:00Z',
  data: {
    article: {
      id: 'article_123',
      title: 'Your Article Title',
      slug: 'your-article-slug',
      locale: 'en-US',
      meta_description: 'Short Article Summary',
      thumbnail: {
        url: 'https://userocketrank.com/img/article.jpg',
        alt: 'Your Article Image Alt Text',
      },
      content_html: 'Article content in HTML',
      content_markdown: 'Article content in Markdown',
      published_at: '2024-01-15T10:30:00Z',
      keywords: ['keyword1', 'keyword2', 'keyword3'],
    },
  },
}

function CopyWebhookPayloadExample() {
  const [isCopied, setIsCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(webhookPayloadExample))
    setIsCopied(true)
    setTimeout(() => {
      setIsCopied(false)
    }, 2000)
  }
  return (
    <Button variant="secondary" size="icon" onClick={handleCopy} type="button">
      {isCopied ? (
        <CopyCheck className="w-4 h-4 text-green-700" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </Button>
  )
}

interface SetupStepProps {
  stepNumber: number
  title: string
  children: React.ReactNode
}

function SetupStep({ stepNumber, title, children }: SetupStepProps) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
        {stepNumber}
      </div>
      <div>
        <h4 className="font-medium text-gray-900 mb-1">{title}</h4>
        {children}
      </div>
    </div>
  )
}

function CreateWebhookEndpointStep() {
  return (
    <SetupStep stepNumber={1} title="Create Your Webhook Endpoint">
      <p className="text-sm text-gray-600 mb-2">
        Set up an HTTP endpoint that can receive POST requests. Here are common
        approaches:
      </p>
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>
            <strong>Express.js (Node.js):</strong> Use{' '}
            <code className="bg-gray-100 px-1 rounded">
              app.post('/webhook', ...)
            </code>
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>
            <strong>Next.js API Route:</strong> Create{' '}
            <code className="bg-gray-100 px-1 rounded">
              pages/api/webhook.js
            </code>{' '}
            or{' '}
            <code className="bg-gray-100 px-1 rounded">
              app/api/webhook/route.js
            </code>
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>
            <strong>Python Flask:</strong> Use{' '}
            <code className="bg-gray-100 px-1 rounded">
              @app.route('/webhook', methods=['POST'])
            </code>
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>
            <strong>Vercel Functions:</strong> Create{' '}
            <code className="bg-gray-100 px-1 rounded">api/webhook.js</code> in
            your project
          </span>
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-2">
        If you need more help, contact support.
      </p>
    </SetupStep>
  )
}

function HandleWebhookRequestStep() {
  return (
    <SetupStep stepNumber={2} title="Handle the Webhook Request">
      <p className="text-sm text-gray-600 mb-2">Your endpoint should:</p>
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-start gap-2">
          <span className="text-green-600">✓</span>
          <span>Accept POST requests with JSON content-type</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-green-600">✓</span>
          <span>
            Verify the{' '}
            <code className="bg-gray-100 px-1 rounded">rr-secret</code> header
            matches your secret key for authentication
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-green-600">✓</span>
          <span>Parse the JSON payload from the request body</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-green-600">✓</span>
          <span>
            Process the article data (save to database, send notifications,
            etc.)
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-green-600">✓</span>
          <span>Return a 200 status code for successful processing</span>
        </div>
        <span>
          We'll notify you if a webhook call fails (any response code other than
          200)
        </span>
      </div>
    </SetupStep>
  )
}

function TestEndpointStep() {
  return (
    <SetupStep stepNumber={3} title="Test Your Endpoint">
      <p className="text-sm text-gray-600 mb-2">
        Before saving your webhook, test it to ensure it works correctly:
      </p>
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>
            Use the "Test Webhook" button above to send a test payload
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>Check your server logs to verify the request was received</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-blue-600">•</span>
          <span>Ensure your endpoint returns a 200 status code</span>
        </div>
      </div>
    </SetupStep>
  )
}

function ExampleCodeSnippets() {
  return (
    <div className="border-t pt-6">
      <h4 className="font-medium text-gray-900 mb-3">Example Implementation</h4>
      <div className="space-y-4">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">
            Express.js Example:
          </h5>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
            <pre>{`app.post('/webhook', express.json(), (req, res) => {
  // Verify the secret key for authentication
  const secretKey = req.headers['rr-secret'];
  const expectedSecret = process.env.ROCKET_RANK_SECRET; // Your secret key
  
  if (secretKey !== expectedSecret) {
    return res.status(401).json({ error: 'Unauthorized' });
  }
  
  const { event, data } = req.body;
  
  if (event === 'article.published') {
    const article = data.article;
    console.log('New article published:', article.title);
    
    // Process the article data here
    // e.g., save to database, send notifications, etc.
  }
  
  res.status(200).json({ success: true });
});`}</pre>
          </div>
        </div>

        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">
            Next.js API Route Example:
          </h5>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
            <pre>{`// pages/api/webhook.js or app/api/webhook/route.js
export default async function handler(req, res) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }
  
  // Verify the secret key for authentication
  const secretKey = req.headers['rr-secret'];
  const expectedSecret = process.env.ROCKET_RANK_SECRET; // Your secret key
  
  if (secretKey !== expectedSecret) {
    return res.status(401).json({ error: 'Unauthorized' });
  }
  
  const { event, data } = req.body;
  
  if (event === 'article.published') {
    const article = data.article;
    console.log('New article published:', article.title);
    
    // Process the article data here
  }
  
  res.status(200).json({ success: true });
}`}</pre>
          </div>
        </div>
      </div>
    </div>
  )
}

function WebhookPayloadPreview() {
  return (
    <div className="border-t pt-6">
      <div className="p-4 bg-gray-50 rounded-lg border">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 mb-2">
            Webhook Payload Preview
          </h4>
          <CopyWebhookPayloadExample />
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Your endpoint will receive a JSON payload like this:
        </p>
        <ReactJsonView
          src={webhookPayloadExample}
          enableClipboard={false}
          quotesOnKeys={false}
        />
      </div>
    </div>
  )
}

// function RecentWebhookCallsCard() {
//   const formatTimestamp = (timestamp: string) => {
//     const date = new Date(timestamp)
//     return date.toLocaleString('en-US', {
//       month: 'short',
//       day: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit',
//       hour12: true,
//     })
//   }

//   const getResponseCodeBadge = (code: number) => {
//     const isSuccess = code >= 200 && code < 300
//     const isClientError = code >= 400 && code < 500
//     const isServerError = code >= 500

//     let bgColor = 'bg-gray-100'
//     let textColor = 'text-gray-800'

//     if (isSuccess) {
//       bgColor = 'bg-green-100'
//       textColor = 'text-green-800'
//     } else if (isClientError) {
//       bgColor = 'bg-yellow-100'
//       textColor = 'text-yellow-800'
//     } else if (isServerError) {
//       bgColor = 'bg-red-100'
//       textColor = 'text-red-800'
//     }

//     return (
//       <span
//         className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}
//       >
//         {code} {status.message[code]}
//       </span>
//     )
//   }

//   return (
//     <Card className="mb-6">
//       <CardHeader>
//         <CardTitle className="flex items-center gap-2">
//           <Clock className="w-5 h-5" />
//           Recent Webhook Calls
//         </CardTitle>
//         <CardDescription>
//           Monitor your webhook delivery status and response times
//         </CardDescription>
//       </CardHeader>
//       <CardContent>
//         <div className="border-border border rounded-xl overflow-y-auto max-h-96">
//           {mockWebhookCalls.map((call) => (
//             <>
//               <button
//                 key={call.id}
//                 className="flex items-center w-full justify-between p-3 hover:bg-accent hover:cursor-pointer"
//               >
//                 <div className="flex items-center gap-3">
//                   <div>
//                     <div className="font-medium text-sm text-gray-900">
//                       {call.event}
//                     </div>
//                     <div className="text-xs text-gray-500 flex items-center gap-2">
//                       <span>{formatTimestamp(call.timestamp)}</span>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="flex items-center gap-2">
//                   {getResponseCodeBadge(call.responseCode)}
//                 </div>
//               </button>{' '}
//               <div className="border-b border-border" />
//             </>
//           ))}
//           <div>
//             <Button variant="link" className="w-full my-4">
//               Load More <ChevronDown className="w-4 h-4" />
//             </Button>
//           </div>
//         </div>
//         {mockWebhookCalls.length === 0 && (
//           <div className="text-center py-8 text-gray-500">
//             <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
//             <p className="text-sm">No webhook calls yet</p>
//             <p className="text-xs">
//               Webhook calls will appear here once you start receiving them
//             </p>
//           </div>
//         )}
//       </CardContent>
//     </Card>
//   )
// }

function SecretKeyInput({ secretKey }: { secretKey: string }) {
  const [showRefreshDialog, setShowRefreshDialog] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  const refreshSecretMutation = useMutation(
    orpc.integrations.refreshIntegrationSecretKey.mutationOptions({
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
      },
      onSuccess: () => {
        toast.success('Secret key refreshed successfully!', {
          description:
            'Your new secret key has been generated. Update your webhook endpoint to use the new key.',
        })
        setShowRefreshDialog(false)
      },
      onError: () => {
        toast.error('Failed to refresh secret key', {
          description: 'Please try again or contact support.',
        })
      },
    }),
  )

  const handleCopy = () => {
    navigator.clipboard.writeText(secretKey)
    setIsCopied(true)
    setTimeout(() => {
      setIsCopied(false)
    }, 2000)
  }

  const handleRefresh = () => {
    refreshSecretMutation.mutate(undefined)
  }

  return (
    <>
      <div className="space-y-2">
        <Label>Secret Key</Label>
        <div className="border border-border rounded-md items-center pl-3 h-10 flex justify-between gap-2">
          <span>{secretKey}</span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              type="button"
              size="icon"
              onClick={handleCopy}
            >
              {isCopied ? (
                <CopyCheck className="w-4 h-4 text-green-700" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              type="button"
              size="icon"
              onClick={() => setShowRefreshDialog(true)}
              disabled={refreshSecretMutation.isPending}
            >
              <RefreshCcw
                className={`w-4 h-4 ${refreshSecretMutation.isPending ? 'animate-spin' : ''}`}
              />
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Use the secret key to authenticate webhook requests from Rocket Rank.
          The secret will be sent in the{' '}
          <code className="bg-gray-100 px-1 rounded">rr-secret</code> header.
        </p>
      </div>

      <Dialog open={showRefreshDialog} onOpenChange={setShowRefreshDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refresh Secret Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to refresh your secret key? This will
              generate a new secret key and invalidate the current one. You'll
              need to update your webhook endpoint to use the new key.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRefresh}
              disabled={refreshSecretMutation.isPending}
            >
              {refreshSecretMutation.isPending ? 'Refreshing...' : 'Refresh'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function RouteComponent() {
  // const [isTesting, setIsTesting] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const integrationsQuery = useQuery(
    orpc.integrations.getIntegrations.queryOptions({
      select: (data) => {
        const customIntegration = data.find((integration) => {
          return integration.config.type === 'custom'
        })
        if (customIntegration?.config.type === 'custom') {
          return {
            ...customIntegration,
            config: customIntegration.config,
          }
        }
        return null
      },
    }),
  )

  const form = useForm({
    resolver: zodResolver(createIntegrationSchema),
    defaultValues: {
      config: {
        type: 'custom',
        url: integrationsQuery.data?.config.url || '',
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      enabled: integrationsQuery.data?.enabled ?? true,
    },
  })

  const createIntegrationMutation = useMutation(
    orpc.integrations.createIntegration.mutationOptions({
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
      },
    }),
  )

  // const testIntegrationRequest = useMutation(
  //   orpc.integrations.testIntegration.mutationOptions({
  //     onMutate: () => {
  //       setIsTesting(true)
  //     },
  //     onSettled: () => {
  //       setIsTesting(false)
  //     },
  //     onSuccess: (result) => {
  //       if (result.success) {
  //         toast.success('Webhook test successful!', {
  //           description: 'Your webhook endpoint responded correctly.',
  //         })
  //       } else {
  //         toast.error('Webhook test failed!', {
  //           description: 'Your webhook endpoint responded incorrectly.',
  //         })
  //       }
  //     },
  //     onError: () => {
  //       toast.error('Webhook test failed!', {
  //         description: 'There was an error on our end. Try again later.',
  //       })
  //     },
  //   }),
  // )

  // const handleTestWebhook = (data: CreateIntegrationSchema) => {
  //   testIntegrationRequest.mutate(data)
  // }

  const onSubmit = async (data: CreateIntegrationSchema) => {
    setIsSubmitting(true)
    try {
      if (data.config.type === 'custom') {
        await createIntegrationMutation.mutateAsync({
          ...data,
          config: {
            ...data.config,
            type: 'custom',
          },
        })

        toast.success('Webhook created successfully!', {
          description:
            'Your custom webhook is now active for published articles.',
        })
      }
    } catch (error) {
      console.error('Failed to create webhook:', error)
      toast.error('Failed to create webhook', {
        description: 'Please try again or contact support.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex gap-3 items-center mb-4">
          <div className="flex gap-3 items-center">
            <Button variant="outline" asChild>
              <Link to="/settings/integrations">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Link>
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Custom Webhook</h1>
          </div>
        </div>
      </div>

      {/* Webhook Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Webhook Configuration</CardTitle>
          <CardDescription>
            Configure your custom webhook to receive published article
            notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormInput
                control={form.control}
                name="config.url"
                label="Webhook URL"
                placeholder="https://example.com/rocketrank/publish"
                description="The URL where we'll send the webhook payload when articles are published"
                formInputProps={{
                  type: 'url',
                  disabled: isSubmitting,
                }}
              />

              <SecretKeyInput
                secretKey={integrationsQuery.data?.config.secretKey || ''}
              />

              <FormField
                control={form.control}
                name={'enabled'}
                render={({ field, formState }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={formState.isSubmitting}
                      />
                    </FormControl>
                    <FormLabel>Enabled</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <MainFormErrorMessage />

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                {/* <Button
                  type="button"
                  variant="outline"
                  onClick={form.handleSubmit(handleTestWebhook)}
                  disabled={
                    isTesting || isSubmitting || !form.formState.isValid
                  }
                >
                  <TestTube className="w-4 h-4" />
                  {isTesting ? 'Testing...' : 'Test Webhook'}
                </Button> */}

                <Button
                  type="submit"
                  disabled={isSubmitting || !form.formState.isValid}
                >
                  <Send className="w-4 h-4" />
                  {isSubmitting ? 'Creating...' : 'Save Webhook'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Recent Webhook Calls */}
      {/* <RecentWebhookCallsCard /> */}

      {/* Setup Help */}
      <Card>
        <CardHeader>
          <CardTitle>Setup Help</CardTitle>
          <CardDescription>
            Follow these step-by-step instructions to set up your webhook
            endpoint
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step-by-step Instructions */}
          <div className="space-y-8">
            <CreateWebhookEndpointStep />
            <HandleWebhookRequestStep />
            <TestEndpointStep />
          </div>

          <ExampleCodeSnippets />
          <WebhookPayloadPreview />
        </CardContent>
      </Card>
    </div>
  )
}
